<script setup lang="ts">
import {
  BoxComponent,
  ButtonComponent,
  DateRangeSelector,
  defaultFormatter,
  FlexComponent,
  formatterProviderKey,
  ImageComponent,
  InputComponent,
  LoadingComponent,
  PaginationComponent,
  SegmentedFilter,
  TooltipComponent,
  TRAFFIC_SOURCE_FILTER_ITEMS,
  TypographyComponent,
} from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import debounce from 'lodash/debounce'
import { computed, inject, ref, watch } from 'vue'

import { useFileDownload } from '@/api/file-download.api'
import { buildLinksExportUrl, useGetLinks } from '@/api/links.api'
import ChevronIcon from '@/assets/icons/chevron.svg?component'
import ExportIcon from '@/assets/icons/export.svg?component'
import LinkIcon from '@/assets/icons/link.svg?component'
import SearchIcon from '@/assets/icons/Magnifier.svg?component'
import { formatDateOnly, formatTimeOnly } from '@/lib/formatters'
import { useAdvertiserPermissions } from '@/lib/hooks/advertiser-permissions.hooks'
import type { LinkListFilter, LinkListSort, LinkListSortOrder } from '@/lib/types/links.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'

const props = withDefaults(
  defineProps<{
    userIds?: number[]
    showCreator?: boolean
  }>(),
  {
    userIds: () => [],
    showCreator: false,
  },
)
const advertiserStore = useAdvertiserStore()
const shopId = computed<number | undefined>(() => advertiserStore.advertiser?.id)
const { hideClicks, hideCosts, hideSales } = useAdvertiserPermissions()
const { formatNumber, formatMoney } = inject(formatterProviderKey, defaultFormatter)
const currency = computed<string>(() => advertiserStore.advertiser?.currencyIsoCode ?? '')
const formatMoneyValue = (amount: number): string => (currency.value ? formatMoney({ amount, currency: currency.value }) : '—')
const filterStore = useGlobalFilterStore()
const page = ref(1)
const sortBy = ref<LinkListSort>('clicks')
const sortOrder = ref<LinkListSortOrder>('desc')
const searchQuery = ref<string | undefined>(undefined)
const filter = computed<LinkListFilter>(() => ({
  page: page.value,
  sortBy: sortBy.value,
  sortOrder: sortOrder.value,
  dateFrom: filterStore.dateRange?.startDate,
  dateTo: filterStore.dateRange?.endDate,
  query: searchQuery.value,
  trafficSourceCode: filterStore.trafficSource,
}))
const userIds = computed<number[]>(() => props.userIds)
watch(
  () => [
    sortBy.value,
    sortOrder.value,
    searchQuery.value,
    filterStore.trafficSource,
    filterStore.dateRange?.startDate,
    filterStore.dateRange?.endDate,
  ],
  () => {
    page.value = 1
  },
)
const { data: links, totalPages, isLoading } = useGetLinks(shopId, filter, userIds)
const { downloadFile, isLoading: isExporting } = useFileDownload()
const searchInput = ref(searchQuery.value ?? '')
const applySearch = debounce((value: string) => {
  searchQuery.value = value || undefined
}, 500)
const handleSearchInput = (value: string) => {
  searchInput.value = value
  applySearch(value)
}
const handleSort = (column: LinkListSort) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'desc'
  }
  page.value = 1
}
const handleExport = async () => {
  if (!shopId.value) {
    return
  }
  await downloadFile(buildLinksExportUrl(shopId.value, filter.value, props.userIds), 'link-stats.xlsx')
}
const NUMERIC_TRACK = 'minmax(96px, 1fr)'
const gridTemplate = computed(() => {
  const tracks = ['minmax(220px, 2fr)']
  if (props.showCreator) {
    tracks.push('minmax(140px, 1.2fr)')
  }
  if (!hideClicks.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideCosts.value) {
    tracks.push(NUMERIC_TRACK, NUMERIC_TRACK)
  }
  if (!hideSales.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideSales.value && !hideCosts.value) {
    tracks.push(NUMERIC_TRACK)
  }
  if (!hideSales.value) {
    tracks.push(NUMERIC_TRACK)
  }
  tracks.push(NUMERIC_TRACK)
  return tracks.join(' ')
})
const sortIndicator = (column: LinkListSort): '' | 'asc' | 'desc' => (filter.value.sortBy === column ? filter.value.sortOrder : '')

const formatCompactNumber = (value: number) => formatNumber(value, 'compact')
const isValueTruncated = (value: number) => formatCompactNumber(value) !== formatNumber(value)
const formatRoas = (roas: number) => formatNumber(roas, 'standard', 'decimal', 2)
const formatCreatedAt = (value: string) => ({
  date: formatDateOnly(value),
  time: formatTimeOnly(value),
})
</script>
<template>
  <BoxComponent class="links-tab">
    <FlexComponent justify="space-between" align="center" :gap="16" class="links-tab__toolbar">
      <SegmentedFilter
        :model-value="filterStore.trafficSource"
        :items="TRAFFIC_SOURCE_FILTER_ITEMS"
        :max="4"
        aria-label="Traffic source"
        @update:model-value="filterStore.setTrafficSource"
      />
      <FlexComponent :gap="12" align="center">
        <BoxComponent class="links-tab__search">
          <InputComponent :model-value="searchInput" placeholder="Search products" size="large" @input="handleSearchInput">
            <template #prefix>
              <ElIcon :size="16">
                <SearchIcon />
              </ElIcon>
            </template>
          </InputComponent>
        </BoxComponent>
        <DateRangeSelector :date-range="filterStore.dateRange" @update:date-range="filterStore.setDateRange" />
        <ButtonComponent variant="accent" :is-loading="isExporting" class="links-tab__export" @click="handleExport">
          <template #iconLeft>
            <ElIcon :size="16">
              <ExportIcon />
            </ElIcon>
          </template>
          Export
        </ButtonComponent>
      </FlexComponent>
    </FlexComponent>
    <BoxComponent class="links-tab__table-wrapper" :style="{ gridTemplateColumns: gridTemplate }">
      <div class="links-tab__heading" role="row">
        <div class="links-tab__heading-cell links-tab__col--product">
          <TypographyComponent variant="label-medium">Product</TypographyComponent>
        </div>
        <div v-if="showCreator" class="links-tab__heading-cell links-tab__col--creator">
          <TypographyComponent variant="label-medium">Creator</TypographyComponent>
        </div>
        <button v-if="!hideClicks" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('clicks')">
          <TypographyComponent variant="label-medium">Clicks</TypographyComponent>
          <ElIcon v-if="sortIndicator('clicks')" :size="18" class="links-tab__sort" :class="`links-tab__sort--${sortIndicator('clicks')}`">
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideCosts" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('cost')">
          <TypographyComponent variant="label-medium">Est. cost</TypographyComponent>
          <ElIcon v-if="sortIndicator('cost')" :size="18" class="links-tab__sort" :class="`links-tab__sort--${sortIndicator('cost')}`">
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideCosts" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('cost_per_click')">
          <TypographyComponent variant="label-medium">Avg. cost/click</TypographyComponent>
          <ElIcon
            v-if="sortIndicator('cost_per_click')"
            :size="18"
            class="links-tab__sort"
            :class="`links-tab__sort--${sortIndicator('cost_per_click')}`"
          >
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideSales" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('order_value')">
          <TypographyComponent variant="label-medium">Order value</TypographyComponent>
          <ElIcon
            v-if="sortIndicator('order_value')"
            :size="18"
            class="links-tab__sort"
            :class="`links-tab__sort--${sortIndicator('order_value')}`"
          >
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideSales && !hideCosts" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('roas')">
          <TypographyComponent variant="label-medium">ROAS</TypographyComponent>
          <ElIcon v-if="sortIndicator('roas')" :size="18" class="links-tab__sort" :class="`links-tab__sort--${sortIndicator('roas')}`">
            <ChevronIcon />
          </ElIcon>
        </button>
        <button v-if="!hideSales" class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('nr_orders')">
          <TypographyComponent variant="label-medium">Orders</TypographyComponent>
          <ElIcon
            v-if="sortIndicator('nr_orders')"
            :size="18"
            class="links-tab__sort"
            :class="`links-tab__sort--${sortIndicator('nr_orders')}`"
          >
            <ChevronIcon />
          </ElIcon>
        </button>
        <button class="links-tab__heading-cell links-tab__col--numeric" @click="handleSort('date')">
          <TypographyComponent variant="label-medium">Created</TypographyComponent>
          <ElIcon v-if="sortIndicator('date')" :size="18" class="links-tab__sort" :class="`links-tab__sort--${sortIndicator('date')}`">
            <ChevronIcon />
          </ElIcon>
        </button>
      </div>
      <BoxComponent v-if="isLoading" class="links-tab__loading">
        <LoadingComponent />
      </BoxComponent>
      <BoxComponent v-else-if="links.length === 0" class="links-tab__empty">
        <TypographyComponent variant="title-medium">No links found</TypographyComponent>
      </BoxComponent>
      <div v-else class="links-tab__rows">
        <div
          v-for="link in links"
          :key="`${link.userId}-${link.originalUrl}-${link.trafficSourceCode ?? 'all'}-${link.createdAt}`"
          class="links-tab__row"
          role="row"
        >
          <div class="links-tab__cell links-tab__col--product">
            <FlexComponent :gap="12" align="center" class="links-tab__product">
              <ImageComponent
                v-if="link.image"
                :image="link.image"
                :width="56"
                :height="56"
                :alt="link.title || 'Product image'"
                class="links-tab__product-image"
              />
              <BoxComponent v-else class="links-tab__product-image links-tab__product-image--placeholder" />
              <FlexComponent direction="column" :gap="4" class="links-tab__product-meta">
                <TypographyComponent variant="title-medium" text-truncate class="links-tab__product-title">
                  {{ link.title || 'Untitled product' }}
                </TypographyComponent>
                <a
                  class="links-tab__product-link"
                  :href="link.originalUrl"
                  :title="link.originalUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ElIcon :size="14"><LinkIcon /></ElIcon>
                  <TypographyComponent variant="body-small" text-truncate class="links-tab__product-url">
                    {{ link.originalUrl }}
                  </TypographyComponent>
                </a>
              </FlexComponent>
            </FlexComponent>
          </div>
          <div v-if="showCreator" class="links-tab__cell links-tab__col--creator">
            <RouterLink v-if="link.userId" :to="{ path: `/creators/${link.userId}` }" class="links-tab__creator-link">
              <TypographyComponent variant="body-medium" text-truncate class="links-tab__creator">
                {{ link.username }}
              </TypographyComponent>
            </RouterLink>
            <TypographyComponent v-else variant="body-medium" text-truncate class="links-tab__creator">
              {{ link.username }}
            </TypographyComponent>
          </div>
          <div v-if="!hideClicks" class="links-tab__cell links-tab__col--numeric">
            <TooltipComponent ref="clicksTooltip" placement="top" :disabled="!isValueTruncated(link.clicks)">
              <TypographyComponent variant="body-medium" :class="{ 'links-tab__clicks': isValueTruncated(link.clicks) }">
                {{ formatCompactNumber(link.clicks) }}
              </TypographyComponent>
              <template #content>
                <TypographyComponent variant="body-medium">{{ formatNumber(link.clicks) }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </div>
          <div v-if="!hideCosts" class="links-tab__cell links-tab__col--numeric">
            <TypographyComponent variant="body-medium">{{ formatMoneyValue(link.cost) }}</TypographyComponent>
          </div>
          <div v-if="!hideCosts" class="links-tab__cell links-tab__col--numeric">
            <TypographyComponent variant="body-medium">{{ formatMoneyValue(link.costPerClick) }}</TypographyComponent>
          </div>
          <div v-if="!hideSales" class="links-tab__cell links-tab__col--numeric">
            <TypographyComponent variant="body-medium">{{ formatMoneyValue(link.orderValue) }}</TypographyComponent>
          </div>
          <div v-if="!hideSales && !hideCosts" class="links-tab__cell links-tab__col--numeric">
            <TypographyComponent variant="body-medium">{{ formatRoas(link.roas) }}</TypographyComponent>
          </div>
          <div v-if="!hideSales" class="links-tab__cell links-tab__col--numeric">
            <TooltipComponent ref="ordersTooltip" placement="top" :disabled="!isValueTruncated(link.nrOfOrders)">
              <TypographyComponent variant="body-medium" :class="{ 'links-tab__orders': isValueTruncated(link.nrOfOrders) }">
                {{ formatCompactNumber(link.nrOfOrders) }}
              </TypographyComponent>
              <template #content>
                <TypographyComponent variant="body-medium">{{ formatNumber(link.nrOfOrders) }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </div>
          <div class="links-tab__cell links-tab__col--numeric links-tab__col--created">
            <TypographyComponent variant="body-medium">{{ formatCreatedAt(link.createdAt).date }}</TypographyComponent>
            <TypographyComponent variant="body-small" class="links-tab__created-time">
              {{ formatCreatedAt(link.createdAt).time }}
            </TypographyComponent>
          </div>
        </div>
      </div>
    </BoxComponent>
    <BoxComponent v-if="totalPages > 1" :mt="24">
      <PaginationComponent v-model:current-page="page" :total-pages="totalPages" />
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.links-tab {
  --row-gap: 12px;
  --row-padding: 24px;
}
.links-tab__toolbar {
  margin-bottom: 24px;
}
.links-tab__search {
  width: 200px;
  :deep(.el-input__wrapper) {
    box-shadow: none;
    background: transparent;
    padding-inline: 0;
    &:hover,
    &.is-focused {
      box-shadow: none;
    }
  }
}
.links-tab__export.links-tab__export {
  :deep(svg) {
    fill: currentColor;
  }
}
.links-tab__table-wrapper {
  display: grid;
  row-gap: var(--row-gap);
}
.links-tab__rows {
  display: contents;
}
.links-tab__heading,
.links-tab__row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  column-gap: 16px;
  align-items: center;
  padding: var(--row-padding);
}
.links-tab__heading {
  padding-block: 8px;
  :deep(.label-medium) {
    font-size: 12px;
    line-height: 18px;
  }
}
.links-tab__row {
  background: var(--inverted);
  border: 1px solid var(--secondary-8);
  border-radius: 16px;
}
.links-tab__row:hover {
  background: #fdfdfd;
}

.links-tab__heading-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: default;
  color: inherit;
  font: inherit;
}
button.links-tab__heading-cell {
  cursor: pointer;
}
.links-tab__sort {
  transition: transform 0.15s ease;
}
.links-tab__sort--asc {
  transform: rotate(0deg);
}
.links-tab__sort--desc {
  transform: rotate(180deg);
}
.links-tab__col--numeric {
  text-align: right;
  justify-content: flex-end;
}
.links-tab__clicks,
.links-tab__orders {
  cursor: default;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  width: fit-content;
  margin-left: auto;
}
.links-tab__col--product {
  text-align: left;
  min-width: 0;
  overflow: hidden;
}
.links-tab__col--creator {
  text-align: left;
  min-width: 0;
  overflow: hidden;
}
.links-tab__creator {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.links-tab__creator-link {
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
}
.links-tab__creator-link:hover {
  text-decoration: underline;
}
.links-tab__product {
  width: 100%;
  min-width: 0;
}
.links-tab__product-image {
  border-radius: 8px;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
}
.links-tab__product-image--placeholder {
  background: var(--secondary-8);
}
.links-tab__product-meta {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}
.links-tab__product-title {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.links-tab__product-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.48;
  text-decoration: none;
  max-width: 100%;
  min-width: 0;
  color: inherit;
  overflow: hidden;
}
.links-tab__product-link:hover {
  text-decoration: underline;
}
.links-tab__product-url {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.links-tab__col--created {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.links-tab__created-time {
  opacity: 0.6;
}
.links-tab__empty,
.links-tab__loading {
  grid-column: 1 / -1;
  padding: 48px;
  text-align: center;
}
</style>
